import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

/**
 * Shape of `enabled` state in context:
 *   { "<path>": { page: boolean, menu: boolean } }
 *
 * Legacy backend responses shaped as `{ "<path>": boolean }` are accepted too,
 * and treated as "both flags equal this bool" (pre-split behavior).
 */
const Ctx = createContext({ enabled: {}, homePath: "/", ready: false });

export function SitePagesProvider({ children }) {
  const [enabled, setEnabled] = useState({});
  const [homePath, setHomePath] = useState("/");
  const [ready, setReady] = useState(false);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-pages`)
      .then(r => {
        // New shape: { pages: {...}, home_path: "/..." }
        // Legacy shape: flat map of paths (pre-Feb-2026)
        const data = r.data || {};
        if (data.pages && typeof data.pages === "object") {
          setEnabled(data.pages);
          setHomePath(data.home_path || "/");
        } else {
          setEnabled(data);
          setHomePath("/");
        }
      })
      .catch(() => { setEnabled({}); setHomePath("/"); })
      .finally(() => setReady(true));
  }, []);
  return <Ctx.Provider value={{ enabled, homePath, ready }}>{children}</Ctx.Provider>;
}

export const useSitePages = () => useContext(Ctx);

export const useHomePath = () => useContext(Ctx).homePath;

/** Returns true if the public page at `path` should be rendered (not blocked
 *  by the visibility toggle). Defaults to true while the config is still
 *  loading so first paint isn't suppressed. */
export function useIsPageEnabled(path) {
  const { enabled, ready } = useSitePages();
  if (!ready) return true;
  const v = enabled[path];
  if (v == null) return true;
  if (typeof v === "boolean") return v;  // legacy shape
  return !!v.page;
}

/** Returns true if this page should appear in the public navigation menu.
 *  Independent of page-visibility — admins can hide a page from the nav while
 *  keeping it reachable via direct URL, or vice versa. */
export function useIsMenuEnabled(path) {
  const { enabled, ready } = useSitePages();
  if (!ready) return true;
  const v = enabled[path];
  if (v == null) return true;
  if (typeof v === "boolean") return v;  // legacy shape → menu == page
  return !!v.menu;
}
